import { useState, useCallback, useMemo, useEffect } from 'react';
import useSWR from 'swr';
import api from 'utils/api';
import type { Artist } from 'types';

interface SearchFilters {
  genres?: string[];
  skills?: string[];
  countries?: string[];
  monthlyListenersBucket?: string;
  maxDistance?: number;
}

interface SearchParams {
  search?: string;
  filters?: SearchFilters;
  coordinates?: { lat: number; lng: number };
  page?: number;
  limit?: number;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  includeFacets?: boolean;
}

interface Facets {
  genres: Array<{ value: string; count: number }>;
  skills: Array<{ value: string; count: number }>;
  countries: Array<{ value: string; count: number }>;
  monthlyListenersBuckets: Array<{
    bucket: 'under-100k' | '100k-500k' | '500k-1m' | 'over-1m';
    count: number;
  }>;
  distanceBuckets?: Array<{
    bucket: 'within-1000' | 'within-5000';
    count: number;
  }>;
}

interface SearchResponse {
  data: Artist[];
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalItems: number;
  };
  facets?: Facets;
}

interface UseCollabClubArtistsSearchResult {
  artists: Artist[];
  facets: Facets | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: any;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
  totalItems: number;
  currentPage: number;
}

export function useCollabClubArtistsSearch(
  searchParams: Omit<SearchParams, 'page' | 'limit' | 'includeFacets'>
): UseCollabClubArtistsSearchResult {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const limit = 8;

  // Build request body for POST
  const buildRequestBody = useCallback(
    (page: number, includeFacets: boolean) => {
      const body: any = {
        page,
        limit,
        includeFacets,
      };

      if (searchParams.search) {
        body.search = searchParams.search;
      }

      if (searchParams.filters) {
        body.filters = {};

        if (searchParams.filters.genres?.length) {
          body.filters.genres = searchParams.filters.genres;
        }

        if (searchParams.filters.skills?.length) {
          body.filters.skills = searchParams.filters.skills;
        }

        if (searchParams.filters.countries?.length) {
          body.filters.countries = searchParams.filters.countries;
        }

        if (
          searchParams.filters.monthlyListenersBucket &&
          searchParams.filters.monthlyListenersBucket !== 'all'
        ) {
          body.filters.monthlyListenersBucket = searchParams.filters.monthlyListenersBucket;
        }

        if (searchParams.filters.maxDistance) {
          body.filters.maxDistance = searchParams.filters.maxDistance;
        }
      }

      if (searchParams.coordinates) {
        body.coordinates = searchParams.coordinates;
      }

      if (searchParams.orderBy) {
        body.orderBy = searchParams.orderBy;
      }

      if (searchParams.orderDirection) {
        body.orderDirection = searchParams.orderDirection;
      }

      return body;
    },
    [searchParams]
  );

  // Generate cache key for the current search parameters (without page)
  const searchKey = useMemo(() => {
    return JSON.stringify({
      search: searchParams.search,
      filters: searchParams.filters,
      coordinates: searchParams.coordinates,
      orderBy: searchParams.orderBy,
      orderDirection: searchParams.orderDirection,
    });
  }, [searchParams]);

  // Fetch first page with facets
  const {
    data: firstPageData,
    error: firstPageError,
    isValidating: isLoadingFirstPage,
  } = useSWR<SearchResponse>(
    ['cc-artists-search', searchKey],
    async () => {
      const body = buildRequestBody(1, true);
      const response = await api.collabClub.searchArtists({ data: body });
      return response as SearchResponse;
    },
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateIfStale: false,
      dedupingInterval: 0,
    }
  );

  // Update state when first page data changes
  useEffect(() => {
    if (firstPageData) {
      setArtists(firstPageData.data);
      setFacets(firstPageData.facets || null);
      setCurrentPage(1);
      setTotalPages(firstPageData.pagination.totalPages);
      setTotalItems(firstPageData.pagination.totalItems);
    }
  }, [firstPageData]);

  // Reset pagination when search parameters change
  useEffect(() => {
    // Only reset pagination, let SWR handle the data updates
    setCurrentPage(1);
  }, [searchKey]);

  // Load more function for infinite scroll
  const loadMore = useCallback(async () => {
    if (isLoadingMore || currentPage >= totalPages) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const body = buildRequestBody(nextPage, false);
      const response = await api.collabClub.searchArtists({ data: body });
      const data = response as SearchResponse;

      setArtists((prev) => [...prev, ...data.data]);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more artists:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, totalPages, isLoadingMore, buildRequestBody]);

  // Reset function to clear all data
  const reset = useCallback(() => {
    setArtists([]);
    setFacets(null);
    setCurrentPage(1);
    setTotalPages(0);
    setTotalItems(0);
  }, []);

  const hasMore = currentPage < totalPages;

  return {
    artists,
    facets,
    isLoading: isLoadingFirstPage,
    isLoadingMore,
    error: firstPageError,
    hasMore,
    loadMore,
    reset,
    totalItems,
    currentPage,
  };
}
